/**
 * SIIIR School Selector Component
 *
 * Searchable combobox for selecting schools from the official SIIIR catalog.
 * Uses shadcn Command + Popover components with paginated search.
 *
 * Features:
 * - Searchable dropdown (3+ characters required)
 * - Paginated results (20 schools per page)
 * - Auto-populate callback when school selected
 * - Integrates with react-hook-form
 * - Design system utilities from main.css
 *
 * Usage with react-hook-form:
 * ```tsx
 * <FormField
 *   control={form.control}
 *   name="codSiiirUnitate"
 *   render={({ field }) => (
 *     <FormItem>
 *       <FormLabel>Cod SIIIR</FormLabel>
 *       <FormControl>
 *         <SiiirSchoolSelector
 *           value={field.value}
 *           onChange={field.onChange}
 *           onSchoolSelect={(school) => {
 *             // Auto-populate other fields
 *             form.setValue('numeInstitutieInvatamant', school.numeInstitutieInvatamant);
 *             form.setValue('cui', school.cui);
 *             // ... etc
 *           }}
 *         />
 *       </FormControl>
 *     </FormItem>
 *   )}
 * />
 * ```
 *
 * Created: 2025-11-02
 * Version: 1.0
 */

import { forwardRef, useState, useEffect, type ComponentProps } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useSchoolImportData } from '@/domains/schools/hooks/useSchoolImportData';
import type { SchoolDataImportDto } from '@/domains/schools/api/schools.api';
import { filtruDeCautareScoala } from './siiir-school-search-filter';

/* =============================================================================
   TYPES
   ============================================================================= */

/**
 * ⚠️ Extinde props-urile unui `<button>`: componenta e montata sub `FormControl`
 * (`components/ui/form.tsx`), care e un `Slot` — pune pe copil `id`,
 * `aria-describedby` si `aria-invalid`. Fara sa le propagam mai departe catre
 * `Button`, ele se pierd tacut: mesajul de eroare apare sub camp, dar campul NU e
 * marcat, deci nu primeste bordura rosie si nu poate fi gasit de scroll-ul catre
 * primul camp invalid. Exact defectul T7/D6, masurat in browser.
 */
interface SiiirSchoolSelectorProps
  extends Omit<ComponentProps<'button'>, 'value' | 'onChange'> {
  /**
   * Selected SIIIR code value
   */
  value?: string;

  /**
   * Change handler for controlled input
   */
  onChange?: (value: string) => void;

  /**
   * Callback when school is selected - provides full school object
   * Use this to auto-populate other form fields
   */
  onSchoolSelect?: (school: SchoolDataImportDto) => void;

  /**
   * Disable the selector
   */
  disabled?: boolean;
}

/* =============================================================================
   COMPONENT
   ============================================================================= */

export const SiiirSchoolSelector = forwardRef<HTMLButtonElement, SiiirSchoolSelectorProps>(function SiiirSchoolSelector({
  value,
  onChange,
  onSchoolSelect,
  disabled = false,
  className,
  ...rest
}: SiiirSchoolSelectorProps, ref) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Use school import data hook with search filter
  const {
    data,
    loading,
    updateFilter,
  } = useSchoolImportData();

  // Debounce real (comentariul de mai sus pretindea unul, dar efectul se declansa la fiecare
  // tasta): golirea campului e imediata, cautarea propriu-zisa asteapta 400ms fara alta tasta.
  useEffect(() => {
    const filtruNou = filtruDeCautareScoala(searchQuery);
    if (filtruNou === null) return;

    if (searchQuery.length === 0) {
      updateFilter(filtruNou);
      return;
    }

    const timer = setTimeout(() => updateFilter(filtruNou), 400);
    return () => clearTimeout(timer);
  }, [searchQuery, updateFilter]);

  // Get schools from paginated response (data is already the array)
  const schools = data || [];

  // Scoala aleasa, memorata local. Resetarea cautarii la inchidere schimba lista incarcata,
  // deci cautarea in `schools` singura ar goli eticheta butonului dupa selectie.
  const [pickedSchool, setPickedSchool] = useState<SchoolDataImportDto | null>(null);

  // Find selected school for display
  const selectedSchool =
    pickedSchool && pickedSchool.codSiiirUnitate === value
      ? pickedSchool
      : schools.find((school) => school.codSiiirUnitate === value);

  // Deschiderea/inchiderea popover-ului. La inchidere se reseteaza si cautarea, ca
  // redeschiderea sa porneasca de la zero, nu cu query-ul si rezultatele filtrate anterior.
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      setSearchQuery('');
    }
  };

  // Handle school selection
  const handleSelect = (school: SchoolDataImportDto) => {
    const newValue = school.codSiiirUnitate || '';

    // Update controlled value
    onChange?.(newValue);
    setPickedSchool(school);

    // Call auto-populate callback
    onSchoolSelect?.(school);

    // Close popover (inchidere programatica: Radix nu cheama onOpenChange aici)
    handleOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          {...rest}
          ref={ref}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('form-input-styled border-input justify-between w-full', className)}
        >
          {selectedSchool ? (
            // Numele, nu codul: valoarea stocata e `codSiiirUnitate`, dar aia e
            // implementare — profesorul isi recunoaste scoala dupa nume (T7/D7).
            <span className="truncate">
              {selectedSchool.numeInstitutieInvatamant}
            </span>
          ) : (
            <span className="text-muted-foreground">
              Alege școala…
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      {/* SE-04: la 360px cu tastatura virtuala deschisa, `avoidCollisions` (implicit true)
          incerca sa flip-uiasca popover-ul DEASUPRA campului cand tastatura ii "manca" spatiul
          de jos -- rezultatul era o lista intoarsa pe dos sau taiata de marginea de sus a
          ecranului. `side="bottom"` + `avoidCollisions={false}` il tin fix sub camp, iar
          `collisionPadding` il opreste sa iasa lipit de marginile ecranului. */}
      <PopoverContent
        className="w-[600px] max-w-[calc(100vw-2rem)] p-0"
        align="start"
        side="bottom"
        avoidCollisions={false}
        collisionPadding={16}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Scrie numele școlii…"
            value={searchQuery}
            onValueChange={setSearchQuery}
            className="h-9"
          />
          {/* Inaltimea fixa de 300px (default-ul din `components/ui/command.tsx`) depaseste
              spatiul vizibil ramas deasupra unei tastaturi virtuale deschise (masurat: 226px din
              612px, `verificare-pe-telefon-real.md`) -- `min(300px,60vh)` se adapteaza. */}
          <CommandList className="max-h-[min(300px,60vh)]">
            <CommandEmpty>
              {loading ? (
                <div className="py-6 text-center text-sm">
                  <Search className="mx-auto h-4 w-4 animate-pulse" />
                  <p className="mt-2">Se caută...</p>
                </div>
              ) : searchQuery.length < 3 ? (
                <div className="py-6 text-center text-sm">
                  <p>Introduceți minim 3 caractere pentru căutare</p>
                </div>
              ) : (
                <div className="py-6 text-center text-sm">
                  <p>Nu au fost găsite școli</p>
                </div>
              )}
            </CommandEmpty>

            {!loading && schools.length > 0 && (
              <CommandGroup>
                {schools.map((school) => (
                  <CommandItem
                    key={school.id || school.codSiiirUnitate}
                    value={school.codSiiirUnitate}
                    onSelect={() => handleSelect(school)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Check
                      className={cn(
                        'h-4 w-4',
                        value === school.codSiiirUnitate
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    <div className="flex-1 truncate">
                      <div className="font-medium truncate">
                        {school.numeInstitutieInvatamant}
                      </div>
                      {/* Localitatea prima: „Scoala Gimnaziala Nr. 1" exista in zeci de
                          localitati, iar codul SIIIR nu spune nimanui nimic (T7/D7). */}
                      <div className="text-sm text-muted-foreground">
                        {[
                          [school.oras || school.comuna || school.sat, school.judet]
                            .filter(Boolean)
                            .join(', '),
                          school.codSiiirUnitate,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {!loading && schools.length >= 20 && (
              <div className="border-t border-gray-200 p-2 text-center text-small text-muted-foreground">
                Se afișează primele 20 rezultate. Rafinați căutarea pentru rezultate mai precise.
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});
